import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

interface Props { heading?: string; subheading?: string; }

export default function FinalCTA({
  heading = 'Ready to Transform Your Outdoor Space?',
  subheading = "Schedule a free on-site consultation. We'll walk your property, discuss your vision, and deliver a detailed proposal — no cost, no obligation.",
}: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => { if (typeof val === 'string') body.append(key, val); });
    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6vu5p67ipaugi04lppg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="estimate" className="w-full relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Hardscaping.jpg"
          alt="Luxury outdoor space by CQC Landscaping"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(7,20,10,0.98) 0%, rgba(7,20,10,0.88) 55%, rgba(7,20,10,0.65) 100%)' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">

          {/* Left: Copy */}
          <div>
            <div className="flex items-center gap-3 mb-7">
              <div className="h-px w-10 bg-gold"></div>
              <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-gold">Free Consultation</span>
            </div>
            <h2 className="font-serif text-white font-semibold leading-tight mb-7" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.2rem)' }}>
              {heading}
            </h2>
            <p className="font-sans text-white/50 text-base leading-relaxed mb-14 max-w-md">{subheading}</p>

            {/* Contact block */}
            <div className="space-y-7">
              <a href="tel:+12086596067" className="flex items-center gap-5 group cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gold/15 group-hover:bg-gold transition-all shrink-0">
                  <i className="ri-phone-fill text-gold group-hover:text-forest-deep text-xl transition-colors"></i>
                </div>
                <div>
                  <div className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/30 mb-1">Call or Text</div>
                  <div className="font-sans font-bold text-white text-2xl group-hover:text-gold transition-colors">(208) 659-6067</div>
                </div>
              </a>
              <a href="mailto:info@cqc-landscaping.com" className="flex items-center gap-5 group cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/6 group-hover:bg-white/12 transition-all shrink-0">
                  <i className="ri-mail-line text-white/50 text-xl"></i>
                </div>
                <div>
                  <div className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/30 mb-1">Email</div>
                  <div className="font-sans text-white/65 text-base">info@cqc-landscaping.com</div>
                </div>
              </a>
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/6 shrink-0">
                  <i className="ri-time-line text-white/50 text-xl"></i>
                </div>
                <div>
                  <div className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/30 mb-1">Hours</div>
                  <div className="font-sans text-white/60 text-sm">Mon–Sat 7:30AM–6PM · Sun 8AM–5PM</div>
                  <div className="font-sans text-gold text-xs mt-0.5">24/7 Emergency Available</div>
                </div>
              </div>
            </div>

            {/* Social proof */}
            <div className="mt-14 flex items-center gap-4 pt-8 border-t border-white/10">
              <div className="flex">{[0,1,2,3,4].map(i => <i key={i} className="ri-star-fill text-gold text-sm"></i>)}</div>
              <span className="font-sans text-white/30 text-xs">5.0 · Google Reviews · Licensed &amp; Insured · Family Owned</span>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
            {/* Gold header */}
            <div className="bg-gold px-8 py-6">
              <h3 className="font-serif text-2xl font-semibold text-forest-deep">Request a Free Estimate</h3>
              <p className="font-sans text-forest/60 text-xs mt-1">We respond within 1 business day — guaranteed.</p>
            </div>

            <div className="p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-forest/8 mb-5">
                    <i className="ri-check-double-line text-forest text-2xl"></i>
                  </div>
                  <h4 className="font-serif text-xl text-forest mb-2">Request Received!</h4>
                  <p className="font-sans text-charcoal-muted text-sm">We'll reach out within 1 business day to confirm your free site visit.</p>
                  <Link to="/" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-forest hover:bg-forest-mid text-white font-semibold text-sm rounded-full px-6 py-3 mt-6 transition-all cursor-pointer">
                    Back to Home
                  </Link>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">Name *</label>
                      <input type="text" name="fullName" required placeholder="John Smith"
                        className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid placeholder-stone focus:outline-none focus:border-gold transition-colors" />
                    </div>
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">Email *</label>
                      <input type="email" name="email" required placeholder="you@email.com"
                        className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid placeholder-stone focus:outline-none focus:border-gold transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">Phone</label>
                      <input type="tel" name="phone" placeholder="(208) 000-0000"
                        className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid placeholder-stone focus:outline-none focus:border-gold transition-colors" />
                    </div>
                    <div>
                      <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">City</label>
                      <input type="text" name="city" placeholder="Hayden, Post Falls..."
                        className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid placeholder-stone focus:outline-none focus:border-gold transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">Service Needed</label>
                    <select name="service" className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid focus:outline-none focus:border-gold transition-colors cursor-pointer">
                      <option value="">Select a service...</option>
                      <option>Custom Landscaping</option>
                      <option>Hardscaping / Patios</option>
                      <option>Retaining Wall</option>
                      <option>Turf &amp; Sod Installation</option>
                      <option>French Drains / Drainage</option>
                      <option>Snow Removal</option>
                      <option>Sprinkler System</option>
                      <option>Full Property Transformation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.12em] uppercase font-bold text-charcoal-mid mb-1.5">
                      Project Details <span className="text-stone font-normal normal-case">({charCount}/500)</span>
                    </label>
                    <textarea name="message" rows={3} maxLength={500} placeholder="Tell us about your project..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-stone/80 rounded-lg px-3.5 py-3 font-sans text-sm text-charcoal-mid placeholder-stone focus:outline-none focus:border-gold transition-colors resize-none"></textarea>
                    {charCount >= 500 && <p className="font-sans text-red-500 text-xs mt-1">Maximum 500 characters.</p>}
                  </div>
                  <button type="submit" disabled={submitting || charCount > 500}
                    className="w-full whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-lg py-4 transition-all cursor-pointer shadow-xl shadow-gold/25 disabled:opacity-60">
                    {submitting ? 'Sending...' : 'Submit Free Estimate Request'}
                    <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'}></i>
                  </button>
                  <p className="font-sans text-stone text-center text-xs">No commitment · Responds within 1 business day</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
